import { z } from 'zod';

// ── Text messages (§36 SMS channel, ADR-069) ───────────────────────────────────

/** An international number (E.164): "+", a country code and the subscriber number, 8–15 digits. */
export const E164_PATTERN = /^\+[1-9][0-9]{7,14}$/;

/**
 * A mobile number as people write it, in E.164 — or null. Spaces, dashes, dots and brackets
 * are dropped; "00" becomes "+"; a number with a leading 0 or none at all gets the
 * institution's country code (`+63`: 0917 123 4567 → +639171234567).
 */
export function normalizePhoneNumber(raw: string, countryCode: string): string | null {
  const compact = raw.trim().replace(/[\s().-]/g, '');
  if (!/^\+?[0-9]+$/.test(compact)) return null;
  let number: string;
  if (compact.startsWith('+')) number = compact;
  else if (compact.startsWith('00')) number = `+${compact.slice(2)}`;
  else if (compact.startsWith('0')) number = `${countryCode}${compact.slice(1)}`;
  else number = `${countryCode}${compact}`;
  return E164_PATTERN.test(number) ? number : null;
}

/** "+63 ••• ••• 4567": enough to recognise one's own number, for screens other people see and logs. */
export function maskPhoneNumber(number: string): string {
  return number.length <= 4 ? '••••' : `${number.slice(0, 3)} ••• ••• ${number.slice(-4)}`;
}

/** Adding a mobile number: checked loosely here, normalised by the server with the country code. */
export const smsNumberSchema = z
  .object({
    number: z
      .string('Enter your mobile number')
      .trim()
      .min(7, 'Enter your mobile number, e.g. 0917 123 4567')
      .max(25, 'That is too long for a mobile number')
      .regex(/^\+?[0-9\s().-]+$/, 'Use digits, and + for a country code'),
  })
  .strict();
export type SmsNumberInput = z.infer<typeof smsNumberSchema>;

/** The six-digit code texted to the number. */
export const smsCodeSchema = z.object({ code: z.string('Enter the code').trim().regex(/^\d{6}$/, 'Enter the 6-digit code from the text message') }).strict();
export type SmsCodeInput = z.infer<typeof smsCodeSchema>;

/** A country calling code, e.g. "+63" (`notifications.sms_country_code`). */
export const countryCallingCodeSchema = z.string().trim().regex(/^\+[1-9][0-9]{0,3}$/, 'A country calling code, e.g. +63');
